import { Component, ReactNode } from 'react'
import * as cn from 'classnames'
import * as moment from 'moment'
import * as numeral from 'numeral'
import * as isNumber from 'lodash/isNumber'
import * as get from 'lodash/get'
import * as commonRowMobileStyles from 'styles/molecules/tables/CommonRowMobile.css'
import { formatTableCurrency } from 'helpers/tableHelpers'
import { TableContextType, TableContext } from 'components/molecules/tables/Table2'
import { AccountType, DetailedAccount } from 'interfaces/accounts'
import { formatUSCurrency } from 'helpers/formatHelpers'
import { CreditAccount } from 'interfaces/credit'
import { CreditscorePageType } from 'interfaces/pages'
import { formatUSDate } from 'helpers/dateTimeHelpers'
import { NOT_APPLICABLE, UNLINKED } from 'constants/accounts'
import { isCreditAccount } from 'helpers/accountHelpers'
import AbsentValue from 'components/atoms/AbsentValue'
import AccountBadge, { AccountBadgeProps } from 'components/features/pfm/AccountBadge/AccountBadge'
import AccountTooltip, { AccountTooltipProps } from 'components/features/pfm/AccountTooltip/AccountTooltip'
import withContext from 'hocs/withContext/withContext'
import { AccountUid } from 'interfaces/linkage'
import { connect } from 'react-redux'
import { disabledAccountsUidsSetSelector } from 'components/features/pageFilters/selectors'
import { createSelector } from 'reselect'

const DATE_FORMAT = 'MMM Do'

export type AccountsDetailsRowMobileOwnProps = {
  table: TableContextType
  row: DetailedAccount
  type: AccountType
} | {
  table: TableContextType
  row: CreditAccount
  type: CreditscorePageType
}

type AccountsDetailsRowMobileStateProps = {
  disabledAccountsSet: Set<AccountUid>
}

type AccountsDetailsRowMobileProps =
  & AccountsDetailsRowMobileOwnProps
  & AccountsDetailsRowMobileStateProps

const processUnlinkedAndManual = (disabledAccountsSet: Set<AccountUid>, fn: (account: DetailedAccount) => ReactNode) => (account: DetailedAccount): ReactNode => {
  const isDisabled = disabledAccountsSet.has(account.uid)
  if (isDisabled) {
    return 'Filtered, not shown'
  }

  if (account.link_status === 'unlinked') {
    return UNLINKED
  }

  if (account.is_manual) {
    return NOT_APPLICABLE
  }

  return fn(account)
}

class AccountsDetailsRowMobile extends Component<AccountsDetailsRowMobileProps, {}> {
  render () {
    const { rowSpanGrid } = this.props.table
    const {
      category,
      name,
      number: cardNumber,
      updated_at: updatedAt,
    } = this.props.row

    const rowKey = this.props.table.getRowKey(this.props.row)
    const amount = this.props.type === 'creditscore' ? this.props.row.balance : this.props.row.amount
    const rowSpan = get(rowSpanGrid, `account_type.${ rowKey }`, 1)

    const spanRow = rowSpan >= 1 ? (
      <div className={ commonRowMobileStyles.spanRow }>{ category.value }</div>
    ) : null

    const statsChildren = this.props.type === 'creditscore'
      ? this.renderCreditStats(this.props.row)
      : (this.props.type === 'debts' ? this.renderAccountStats(this.props.row) : null)

    // this is a sacrifice to a TS god
    const accountTooltipProps: AccountTooltipProps = this.props.type === 'creditscore'
      ? { type: this.props.type, account: this.props.row }
      : { type: this.props.type, account: this.props.row }

    const accountBadgeProps: AccountBadgeProps = {
      name,
      cardNumber,
      cardType: this.props.type !== 'creditscore' ? this.props.row.card_type : undefined,
      updatedAt,
      tooltipChild: (
        <AccountTooltip { ...accountTooltipProps } />
      ),
    }

    let value: ReactNode = formatTableCurrency(amount, 'penny')

    if (this.props.disabledAccountsSet.has(this.props.row.uid)) {
      value = (
        <AbsentValue>Filtered, not shown</AbsentValue>
      )
    } else if (!isCreditAccount(this.props.row) && this.props.row.link_status === 'unlinked') {
      value = this.props.type === 'debts'
        ? ''
        : <AbsentValue>{ UNLINKED }</AbsentValue>
    }

    return (
      <tr key={ rowKey }>
        <td>
          { spanRow }
          <div className={ commonRowMobileStyles.commonRowMobile }>
            <div className={ commonRowMobileStyles.line }>
              <div className={ cn(commonRowMobileStyles.linePart, commonRowMobileStyles.leftAligned) }>
                <AccountBadge { ...accountBadgeProps } />
              </div>
              <div className={ cn(commonRowMobileStyles.linePart, commonRowMobileStyles.right) }>
                <div className={ commonRowMobileStyles.value }>{ value }</div>
              </div>
            </div>

            <div className={ commonRowMobileStyles.statsLine }>
              { statsChildren }
            </div>
          </div>
        </td>
      </tr>
    )
  }

  renderDueString = processUnlinkedAndManual(
    this.props.disabledAccountsSet,
    (account: DetailedAccount): ReactNode => {
      const { due_date, minimum_amount_due } = account

      let dueString = ''
      if (isNumber(minimum_amount_due)) {
        dueString += `${ formatUSCurrency(minimum_amount_due) }`
        if (due_date) {
          dueString += ` on `
        }
      }

      if (due_date) {
        dueString += moment(due_date).format(DATE_FORMAT)
      }

      return dueString || NOT_APPLICABLE
    }
  )

  renderRate = processUnlinkedAndManual(
    this.props.disabledAccountsSet,
    (account: DetailedAccount): ReactNode => {
      const { rate } = account

      return isNumber(rate) ? numeral(rate / 100).format('0.00%') : NOT_APPLICABLE
    }
  )

  renderCreditStats = (account: CreditAccount) => {
    const { condition, pay_status, opened_at } = account

    return (
      <>
        <div
          className={ cn(
            commonRowMobileStyles.statBlock,
            commonRowMobileStyles.creditConditionStatBlock,
            commonRowMobileStyles.leftAligned,
          ) }
        >
          <div className={ commonRowMobileStyles.statCaption }>
            Condition
          </div>
          <div className={ commonRowMobileStyles.statValue }>
            { condition }
          </div>
        </div>
        <div className={ cn(commonRowMobileStyles.statBlock, commonRowMobileStyles.leftAligned) }>
          <div className={ commonRowMobileStyles.statCaption }>
            Pay Status
          </div>
          <div className={ commonRowMobileStyles.statValue }>
            { pay_status }
          </div>
        </div>
        <div
          className={ cn(
            commonRowMobileStyles.statBlock,
            commonRowMobileStyles.dateOpenedStatBlock,
            commonRowMobileStyles.leftAligned
          ) }
        >
          <div className={ commonRowMobileStyles.statCaption }>
            Date Opened
          </div>
          <div className={ commonRowMobileStyles.statValue }>
            { formatUSDate(opened_at) }
          </div>
        </div>
      </>
    )
  }

  renderAccountStats = (account: DetailedAccount) => {
    return (
      <>
        <div className={ cn(commonRowMobileStyles.statBlock, commonRowMobileStyles.leftAligned) }>
          <div className={ commonRowMobileStyles.statCaption }>
            Interest Rate
          </div>
          <div className={ commonRowMobileStyles.statValue }>
            { this.renderRate(account) }
          </div>
        </div>
        <div className={ cn(commonRowMobileStyles.statBlock, commonRowMobileStyles.rightAligned) }>
          <div className={ commonRowMobileStyles.statCaption }>
            Next Payment
          </div>
          <div className={ commonRowMobileStyles.statValue }>
            { this.renderDueString(account) }
          </div>
        </div>
      </>
    )
  }
}


const mapStateToProps = createSelector(
  disabledAccountsUidsSetSelector,
  (disabledAccountsSet) => ({
    disabledAccountsSet,
  })
)


const withTableContext = withContext(TableContext, 'table')(AccountsDetailsRowMobile)
const connected = connect<AccountsDetailsRowMobileStateProps>(mapStateToProps)(withTableContext)

export default connected
